/* eslint-disable max-lines */
import {
  AutoComplete,
  Box,
  Button,
  CustomModalLayout,
  DropdownLayoutValueOption,
  FloatingNotification,
  FormField,
  Heading,
  InputArea,
  Loader,
  ModalMobileLayout,
  Text,
} from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import React, { FunctionComponent, useContext } from 'react';
import { DATA_HOOKS } from '../../dataHooks';
import css from './index.module.scss';
import { StatusAlert } from 'wix-ui-icons-common';
import { useBI } from '../../hooks/useBI';
import { roadmapWishlistFormClick } from '@wix/bi-logger-new-help-center/v2';
import { BI, EXPERIMENTS } from '../../constants';
import { isMobile } from '../../utils/userAgent';
import { Context } from '../../context';
import { COLORS } from '../../utils/theme';
import { RoadmapFeatureRequestSuccessModal } from './RoadmapFeatureRequestSuccessModal';
import { RoadmapFeatureRequestSuggestionModal } from './RoadmapFeatureRequestSuggestionModal';
import { Article } from '@wix/answers-api';
import { useExperiments, useHttpClient } from '@wix/fe-essentials-standalone';
import { useRouter } from 'next/router';
import { ArticleType } from '@wix/ambassador-answers-v1-ai-wrapper/build/cjs/types.impl';

const EDITOR_X_LABEL_ID = '3008b109-b7ca-45cc-b80b-942a91fac828';
export const sanitizeNewLinesForMonday = (text: string) =>
  text.replace(/\n/g, ' ; ');

export type RoadmapFeatureRequestModalProps = {
  labels: {
    id: string;
    name: string;
  }[];
  onSend: (
    productName: string,
    description: string,
    impact: string
  ) => Promise<void>;
  onClose: () => void;
  onVote?: (articleId: string) => void;
};
export const RoadmapFeatureRequestModal: FunctionComponent<
  RoadmapFeatureRequestModalProps
> = ({ labels, onSend, onClose, onVote }) => {
  const { t } = useTranslation();
  const { experiments } = useExperiments({ readOnly: true });
  const httpClient = useHttpClient();
  const { locale } = useRouter();
  const [productLabel, setProductLabel] = React.useState<{
    id: string;
    name: string;
  }>();
  const [product, setProduct] = React.useState('');
  const [isLoading, setLoading] = React.useState(false);
  const [showRequestError, setShowRequestError] = React.useState(false);
  const [description, setDescription] = React.useState<string>();
  const [impactDescription, setImpactDescription] = React.useState<string>();
  const [showErrorMsg, setShowErrorMsg] = React.useState(false);
  const { sendBIEvent } = useBI();
  const context = useContext(Context);
  const isMobileView = isMobile(context);

  const [showSuccessModal, setShowSuccessModal] = React.useState(false);
  const [showVoteModal, setShowVoteModal] = React.useState(false);
  const [suggestionArticle, setSuggestionArticle] = React.useState<
    Article | undefined
  >(undefined);

  const isSuggestModalEnabled = experiments.enabled(
    EXPERIMENTS.SPECS.SUGGEST_SIMILAR_FR
  );

  const productList = labels
    .filter((l) => l.id !== EDITOR_X_LABEL_ID)
    .map((l) => ({
      id: l.id,
      value: l.name.trimStart(),
    }));

  const predicate = (option: DropdownLayoutValueOption) =>
    `${option.value}`.toLowerCase().indexOf(product.toLowerCase()) !== -1;

  const onSelectProduct = (option: DropdownLayoutValueOption) => {
    setShowErrorMsg(false);
    setProductLabel({ id: `${option.id}`, name: `${option.value}` });
    setProduct(`${option.value}`.trimStart());
  };

  const onChangeValue: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    setShowErrorMsg(false);
    setProductLabel(undefined);
    setProduct(event.target.value);
  };

  const onChangeDescription: React.ChangeEventHandler<HTMLTextAreaElement> = (
    e
  ) => {
    setShowErrorMsg(false);
    setDescription(e.target.value);
  };

  const onChangeImpact: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setImpactDescription(e.target.value.trim());
  };

  const mobileLabel = (text: string, mandatory?: boolean) => (
    <Text secondary className={css.mobileLabel} color={COLORS.D20} size="small">
      {text}
      {mandatory && <span className={`${css.asterisk}`}>*</span>}
    </Text>
  );

  const getSuggestArticle = async () => {
    if (productLabel && description) {
      setLoading(true);
      try {
        const { data } = await httpClient.get<Article>(
          '/api/roadmap/articleSuggestion',
          {
            params: {
              query: description,
              numOfDocs: 1,
              product: productLabel?.name || '',
              locale,
              dataSourceDocType: ArticleType.FEATURE_REQUEST,
            },
          }
        );

        setSuggestionArticle(data);
        setLoading(false);
      } catch (error) {
        await onSendFR();
      }
    } else {
      setShowErrorMsg(true);
    }
  };

  const onVoteArticle = (articleId: string) => {
    if (onVote) {
      onVote(articleId);
      setShowVoteModal(true);
    }
  };

  const floatingErrorNotification = showRequestError ? (
    <Box position="absolute" top="44px" left="50%" transform="translateX(-50%)">
      <FloatingNotification
        prefixIcon={<StatusAlert />}
        type="destructive"
        text={t('feature-request.modal.select-product.error-notification')}
        onClose={() => setShowRequestError(false)}
      />
    </Box>
  ) : null;

  const modalBody = (
    <Box
      className={css.featureRequestModalBodyWrapper}
      direction="vertical"
      gap="SP3"
      paddingBottom="SP2"
    >
      {floatingErrorNotification}
      <FormField
        required={!isMobileView}
        label={
          isMobileView
            ? mobileLabel(t('feature-request.modal.select-product.title'), true)
            : t('feature-request.modal.select-product.title')
        }
        statusMessage={
          showErrorMsg &&
          !productLabel &&
          t('feature-request.modal.select-product.invalid')
        }
        status={showErrorMsg && !productLabel ? 'error' : undefined}
      >
        <AutoComplete
          value={product}
          size={isMobileView ? 'small' : 'medium'}
          dataHook={DATA_HOOKS.ROADMAP_FEATURE_REQUEST_MODAL_SELECT_PRODUCT}
          onSelect={onSelectProduct}
          onChange={onChangeValue}
          className={css.roadmapFilterMenu}
          placeholder={t('feature-request.modal.select-product.placeholder')}
          options={productList}
          predicate={predicate}
          popoverProps={{
            appendTo: 'parent',
          }}
          emptyStateMessage={t(
            'feature-request.modal.select-product.empty-state',
            { value: product }
          )}
        />
      </FormField>
      <FormField
        required
        label={t('feature-request.modal.description.title')}
        statusMessage={
          showErrorMsg &&
          !description &&
          t('feature-request.modal.description.invalid')
        }
        status={
          showErrorMsg && (!description || !description.trim().length)
            ? 'error'
            : undefined
        }
      >
        <InputArea
          placeholder={t('feature-request.modal.description.placeholder')}
          tooltipPlacement="top-end"
          onChange={onChangeDescription}
          minHeight={'96px'}
          size={isMobileView ? 'small' : 'medium'}
          value={description}
          dataHook={DATA_HOOKS.ROADMAP_FEATURE_REQUEST_MODAL_DESCRIPTION}
        />
      </FormField>
      <FormField
        label={
          isMobileView
            ? mobileLabel(t('feature-request.modal.impact-description.title'))
            : t('feature-request.modal.impact-description.title')
        }
      >
        <InputArea
          placeholder={t(
            'feature-request.modal.impact-description.placeholder'
          )}
          tooltipPlacement="top-end"
          onChange={onChangeImpact}
          minHeight={'96px'}
          size={isMobileView ? 'small' : 'medium'}
          value={impactDescription}
          dataHook={DATA_HOOKS.ROADMAP_FEATURE_REQUEST_MODAL_IMPACT}
        />
      </FormField>
    </Box>
  );

  const onCancel = async () => {
    onClose();
    await sendBIEvent(
      roadmapWishlistFormClick({
        source_name: BI.SOURCE_NAMES.ROADMAP,
        clicked_item_type: BI.CLICKED_ITEM_TYPES.CANCEL,
        kb_lang: locale as string,
      })
    );
  };

  const onSendFR = async () => {
    if (productLabel && description) {
      setLoading(true);
      try {
        const descriptionForMonday = sanitizeNewLinesForMonday(
          description.trim()
        );
        const impactForMonday = impactDescription
          ? sanitizeNewLinesForMonday(impactDescription.trim())
          : '';
        await onSend(productLabel.name, descriptionForMonday, impactForMonday);
      } catch (e) {
        setShowRequestError(true);
      }
      setLoading(false);
      if (!isMobileView) {
        setShowSuccessModal(true);
      }
    } else {
      setShowErrorMsg(true);
    }
  };

  const onSendButtonDisabled =
    !productLabel && !description && !impactDescription;

  return isMobileView ? (
    <ModalMobileLayout
      title={
        <Box direction="vertical">
          <Heading appearance="H2">{t('feature-request.modal.title')}</Heading>
          <Text weight="thin">{t('feature-request.modal.subtitle')}</Text>
        </Box>
      }
      footer={
        <Box gap="SP2" direction="horizontal" align="right">
          <Button size="small" priority="secondary" onClick={onCancel}>
            {t('feature-request.modal.cancel')}
          </Button>
          <Button size="small" onClick={onSendFR}>
            {isLoading ? (
              <Loader size="tiny" />
            ) : (
              t('feature-request.modal.send')
            )}
          </Button>
        </Box>
      }
      content={modalBody}
      onCloseButtonClick={onClose}
      dataHook={DATA_HOOKS.ROADMAP_FEATURE_REQUEST_MODAL_BODY}
    />
  ) : showSuccessModal || showVoteModal || isLoading ? (
    <RoadmapFeatureRequestSuccessModal
      isLoading={isLoading}
      onClose={onClose}
      resolution={showVoteModal ? suggestionArticle?.resolution : undefined}
    />
  ) : isSuggestModalEnabled && suggestionArticle ? (
    <RoadmapFeatureRequestSuggestionModal
      onSend={onSendFR}
      onBack={() => setSuggestionArticle(undefined)}
      onClose={onClose}
      onVote={onVoteArticle}
      article={suggestionArticle}
    />
  ) : (
    <CustomModalLayout
      primaryButtonText={
        isLoading ? <Loader size="tiny" /> : t('feature-request.modal.send')
      }
      className={css.featureRequestModal}
      primaryButtonProps={{ disabled: onSendButtonDisabled }}
      secondaryButtonText={t('feature-request.modal.cancel')}
      secondaryButtonOnClick={onCancel}
      primaryButtonOnClick={
        isSuggestModalEnabled ? getSuggestArticle : onSendFR
      }
      onCloseButtonClick={onClose}
      title={t('feature-request.modal.title')}
      subtitle={t('feature-request.modal.subtitle')}
      content={modalBody}
      width={!isMobileView ? '600px' : '360px'}
      dataHook={DATA_HOOKS.ROADMAP_FEATURE_REQUEST_MODAL_BODY}
    />
  );
};
